import { ShieldAlert } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Steps, Callout, RoleBadge, pathFor,
  Diagram, MockTabs, MockTable, MockButton, MockRow,
} from "../shared";

export default function PagesEscalationsSection() {
  return (
    <div data-testid="guide-pages-escalations">
      <PageHeading
        icon={ShieldAlert}
        title="Escalations Inbox"
        description="A review queue for 'stalled-task' concerns raised by people who couldn't flag dropped-the-ball behaviour themselves. Senior reviewers decide whether the person who was sitting on the work gets a mark against their record."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="admin" />
        <span className="text-xs text-white/40">Only users with the <span className="font-mono text-white/70">confirm_dropped_the_ball</span> permission can open this page.</span>
      </div>

      <Callout variant="info" title="In plain words">
        Sometimes a task gets stuck with someone and a colleague has to take it over. If they felt the work was genuinely dropped, they can raise a flag. This page is where a <span className="text-white">senior reviewer</span> reads that flag and decides, fairly, whether it should count against anyone's record — nothing happens automatically.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2">
          <li>When you're a senior reviewer who has been sent a stalled-task review.</li>
          <li>To decide whether a person's KPI gets a strike, or whether the concern is dismissed.</li>
          <li>To keep a clear, fair record of how each escalation was handled.</li>
        </ul>
      </Section>

      <Section title="Where escalations come from">
        <p className="text-xs text-white/60 leading-relaxed">
          When a regular user reassigns someone else's item and picks the <span className="text-white">Escalate as dropped-the-ball</span> reason in the handoff form (see <Link href={pathFor("forms/handoff")} className="text-cyan-300 hover:underline">Hand off responsibility</Link>), three things happen at once:
        </p>
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2 mt-2">
          <li>The reassignment goes through immediately — work doesn't wait for the review.</li>
          <li>The named senior reviewer gets an email titled <span className="font-mono text-white">"Stalled-task review requested: …"</span>.</li>
          <li>An open row appears on this page for that reviewer (and for anyone else with the permission, via the "All open" toggle).</li>
        </ul>
        <p className="text-xs text-white/40 mt-2">
          The person who was holding the work gets only the standard, neutral reassignment email. They are <span className="text-white">not</span> told an escalation was raised — that conversation is meant to happen face-to-face.
        </p>
      </Section>

      <Section title="A picture of the page">
        <Diagram
          frameLabel="Escalations Inbox"
          legend={[
            <>The <span className="text-white">Mine</span> tab shows escalations addressed to you; <span className="text-white">All open</span> shows every unresolved one across the organisation.</>,
            <>Each row shows the item (click to open in a new tab), who raised it, the displaced holder, who holds it now, and the raiser's reason.</>,
            <><span className="text-white">Confirm KPI flag</span> records the concern — the displaced holder's <span className="text-white">Pulled (Stalled)</span> metric goes up by one.</>,
            <><span className="text-white">Dismiss</span> closes it with no change to anyone's KPI; the reassignment itself is not reversed.</>,
          ]}
        >
          <MockTabs
            tabs={[
              { label: "Mine", active: true, marker: 1 },
              { label: "All open", marker: 1 },
            ]}
          />
          <MockTable
            columns={["Item", "Raised by", "Displaced holder", "Now with", "Reason"]}
            rows={[
              ["Q3 portfolio pack", "Sara K.", "Amal R.", "Ravi P.", "No progress in 2 weeks"],
            ]}
          />
          <MockRow>
            <MockButton label="Confirm KPI flag" marker={3} />
            <MockButton label="Dismiss" marker={4} variant="ghost" />
          </MockRow>
        </Diagram>
        <Steps
          steps={[
            <>Choose <span className="text-white">Mine</span> for escalations addressed to you, or <span className="text-white">All open</span> to see everything unresolved.</>,
            <>Read the <span className="text-white">row</span> — who raised it, who was holding the work, who has it now, and why. Click the item title to open its detail page in a new tab.</>,
            <>Press <span className="text-white">Confirm KPI flag</span> if the concern is fair — this adds an <span className="font-mono text-white">ESCALATION_RESOLVED</span> event with <span className="font-mono text-white">outcome=INSUFFICIENT_PROGRESS</span> and increments the holder's Pulled (Stalled) metric.</>,
            <>Otherwise press <span className="text-white">Dismiss</span> — this records <span className="font-mono text-white">outcome=DISMISSED</span> and leaves all KPIs untouched. A resolution note is optional but recommended; it's stored in the audit trail.</>,
          ]}
        />
      </Section>

      <Callout variant="info" title="Permission and visibility">
        Only users with the <span className="font-mono text-white">confirm_dropped_the_ball</span> permission see the Escalations sidebar link and can open this page. By default that permission is granted to the <span className="text-white">C-suite</span> preset role and to <span className="text-white">Super Admin</span>.
      </Callout>
    </div>
  );
}
